import React from 'react'
import { RouteObject } from 'react-router-dom'

import {
  AuthProvider,
  Head,
  HomeHead,
  LoginCallback,
  OptionIndexHead,
  PaymentHead,
  PlanIndexHead,
  PlanListIndexHead,
  PropertyDetailHead,
  PropertyProvider,
  PropertySeoHead,
  RegistrationCompletionHead,
  RoomIndexHead,
} from '../components'
import { PATH, meta, reservationRouteType } from '../constants'
import {
  CancellationCompletionFrame,
  CancellationIndexFrame,
  LoginErrorFrame,
  NotFoundErrorFrame,
  OptionPlanIndexFrame,
  PlanIndexFrame,
  PlanListIndexFrame,
  PlanRoomIndexFrame,
  PropertyDetailFrame,
  PropertyListFrame,
  RegistrationCompletionFrame,
  RegistrationConfirmationFrame,
  RegistrationLoadingFrame,
  RegistrationNewFrame,
  ReservationDetailFrame,
  ReservationIndexFrame,
  RoomIndexFrame,
  ScheduleListFrame,
  SearchIndexFrame,
  ServerErrorFrame,
  TopIndexFrame,
} from '../frames'

const OAUTH_PROVIDER = process.env.VITE_OAUTH_PROVIDER || 'fid'
// f-id not allowed to use in localhost.
// So in development and staging env we use Google Oatuh2 instead of f-id.
const loginRoutes: RouteObject =
  OAUTH_PROVIDER === 'google'
    ? {
        path: PATH.login.root,
        children: [
          {
            path: PATH.login.google,
            element: <LoginCallback />,
          },
        ],
      }
    : {
        path: PATH.login.root,
        children: [
          {
            path: PATH.login.fid,
            element: <LoginCallback />,
          },
        ],
      }

const reservationRoutes = Object.values(reservationRouteType).map((type) => ({
  path: PATH.reservations[type].root,
  children: [
    {
      index: true,
      element: (
        <AuthProvider>
          <Head {...meta.reservations.root}>
            <ReservationIndexFrame reservationType={type} />
          </Head>
        </AuthProvider>
      ),
    },
    {
      path: PATH.reservations[type].history,
      element: (
        <AuthProvider>
          <Head {...meta.reservations.history}>
            <ReservationIndexFrame reservationType={type} />
          </Head>
        </AuthProvider>
      ),
    },
    {
      path: PATH.reservations[type].reservation,
      element: (
        <AuthProvider>
          <Head {...meta.reservations.reservation}>
            <ReservationDetailFrame />
          </Head>
        </AuthProvider>
      ),
    },
    {
      path: PATH.reservations[type].cancel.root,
      children: [
        {
          index: true,
          element: (
            <AuthProvider>
              <Head {...meta.reservations.cancel.root}>
                <CancellationIndexFrame />
              </Head>
            </AuthProvider>
          ),
        },
        {
          path: PATH.reservations[type].cancel.completion,
          element: (
            <AuthProvider>
              <Head {...meta.reservations.cancel.completion}>
                <CancellationCompletionFrame />
              </Head>
            </AuthProvider>
          ),
        },
      ],
    },
  ],
}))

export const routes: RouteObject[] = [
  {
    path: PATH.root,
    element: (
      <React.Fragment>
        <HomeHead />
        <TopIndexFrame />
      </React.Fragment>
    ),
  },
  {
    path: PATH.list,
    element: (
      <Head {...meta.list}>
        <PropertyListFrame />
      </Head>
    ),
  },
  {
    path: PATH.schedule,
    element: (
      <Head {...meta.schedule}>
        <ScheduleListFrame />
      </Head>
    ),
  },
  {
    path: PATH.search,
    element: (
      <Head {...meta.search}>
        <SearchIndexFrame />
      </Head>
    ),
  },
  {
    path: PATH.property,
    element: (
      <PropertyProvider>
        <PropertyDetailHead />
        <PropertyDetailFrame />
      </PropertyProvider>
    ),
  },
  {
    path: PATH.rooms,
    element: (
      <PropertyProvider>
        <PropertySeoHead {...meta.rooms}>
          <RoomIndexHead />
          <RoomIndexFrame />
        </PropertySeoHead>
      </PropertyProvider>
    ),
  },
  {
    path: PATH.plan.rooms,
    element: (
      <PropertyProvider>
        <PropertySeoHead {...meta.planRooms}>
          {/* Plan-first room list is the same room-list landing experience as
              `PATH.rooms`, so emit the same property-level `view_item`
              ecommerce event for consistent dataLayer instrumentation. */}
          <RoomIndexHead />
          <PlanRoomIndexFrame />
        </PropertySeoHead>
      </PropertyProvider>
    ),
  },
  {
    path: PATH.propertyPlans,
    element: (
      <PropertyProvider>
        <PropertySeoHead {...meta.propertyPlans}>
          {/* Property-level (room-independent) ecommerce event — see
              PlanListIndexHead. Do NOT reuse PlanIndexHead (room-scoped). */}
          <PlanListIndexHead />
          <PlanListIndexFrame />
        </PropertySeoHead>
      </PropertyProvider>
    ),
  },
  {
    path: PATH.plans,
    element: (
      <PropertyProvider>
        <PropertySeoHead {...meta.plans}>
          <PlanIndexHead />
          <PlanIndexFrame />
        </PropertySeoHead>
      </PropertyProvider>
    ),
  },
  {
    path: PATH.options,
    element: (
      <PropertyProvider>
        <PropertySeoHead {...meta.options}>
          <OptionIndexHead />
          <OptionPlanIndexFrame />
        </PropertySeoHead>
      </PropertyProvider>
    ),
  },
  loginRoutes,
  {
    path: PATH.registrations.root,
    children: [
      {
        index: true,
        element: (
          <AuthProvider>
            <PropertyProvider>
              <Head {...meta.registrations.root}>
                <PaymentHead />
                <RegistrationNewFrame />
              </Head>
            </PropertyProvider>
          </AuthProvider>
        ),
      },
      {
        path: PATH.registrations.confirmation,
        element: (
          <AuthProvider>
            <PropertyProvider>
              <Head {...meta.registrations.confirmation}>
                <RegistrationConfirmationFrame />
              </Head>
            </PropertyProvider>
          </AuthProvider>
        ),
      },
      {
        path: PATH.registrations.completion,
        element: (
          <AuthProvider>
            <PropertyProvider>
              <Head {...meta.registrations.completion}>
                <RegistrationCompletionHead />
                <RegistrationCompletionFrame />
              </Head>
            </PropertyProvider>
          </AuthProvider>
        ),
      },
      {
        path: PATH.registrations.loading,
        element: (
          <AuthProvider>
            <PropertyProvider>
              <Head {...meta.registrations.loading}>
                <RegistrationLoadingFrame />
              </Head>
            </PropertyProvider>
          </AuthProvider>
        ),
      },
    ],
  },
  ...reservationRoutes,
  {
    path: PATH.errors.serverError,
    element: (
      <Head {...meta.errors.serverError}>
        <ServerErrorFrame />
      </Head>
    ),
  },
  {
    path: PATH.errors.loginError,
    element: (
      <Head {...meta.errors.loginError}>
        <LoginErrorFrame />
      </Head>
    ),
  },
  {
    path: PATH.errors.notFoundError,
    element: (
      <Head {...meta.errors.loginError}>
        <NotFoundErrorFrame />
      </Head>
    ),
  },
]
